import {
  type ShortCode,
  type EnrichmentContext,
  type TransformedItemPost,
  fetchEmbeddedPosts,
  fetchEmbeddedPostsByCat,
  withDefaultContributors,
} from '@highereducation/wordpress';
import {
  ArticleRow,
  type ArticleRowProps,
  type ArticleCardProps,
  type GridGap,
} from '@highereducation/hen-ui';

const GRID_GAPS = [
  'xxs',
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  '2xl',
  '3xl',
  'max',
] as const satisfies readonly GridGap[];

interface ArticleRowShortcodeProps
  extends Omit<ArticleRowProps, 'articles' | 'variant'> {
  variant: 'hero' | '2-col' | '3-col' | '4-col' | 'carousel' | 'vertical';
  'hero-image'?: string;
  'hero-brow'?: string;
  'hero-title'?: string;
  'hero-subtext'?: string;
  'hero-action-label'?: string;
  'hero-action-href'?: string;
  'hero-action-target'?: React.HTMLAttributeAnchorTarget;
  'hero-action-data-hen-location'?: string;
  'hero-action-data-hen-name'?: string;
  'show-image'?: string | boolean;

  /** hero overlay: `left` | `center` (default center) */
  align?: string;
  ids?: string;
  category?: string;
  latest?: string;
  order?: string;
  limit?: string;
  size?: ArticleCardProps['size'];
  border?: ArticleCardProps['border'];
  'card-variant'?: string;
  'card-layout'?: string;
  'hover-style'?: 'none' | 'shadow';
  link?: string;
  'link-label'?: string;
  'link-variant'?: string;
  'show-brow'?: string | boolean;
  gap?: string;
  children?: React.ReactNode;
  [key: string]: unknown;
}

import {
  parseArticleRowVariant,
  pickHenMetadataAttrs,
  parseShowImage,
  parseTruthy,
  toArticleCardProps,
} from '../utils';

const parseHeroOverlayAlign = (val?: string): 'left' | 'center' =>
  (val ?? '').toLowerCase().trim() === 'left' ? 'left' : 'center';

const LINK_VARIANTS = [
  'primary',
  'secondary',
  'tertiary',
  'default',
  'black',
  'white',
  'subtext',
] as const;
type LinkVariant = (typeof LINK_VARIANTS)[number];

/** Maps the `link-variant` attr to a valid Link variant; invalid/omitted = undefined (base color). */
const parseLinkVariant = (val?: string): LinkVariant | undefined => {
  const v = (val ?? '').toLowerCase().trim();
  return (LINK_VARIANTS as readonly string[]).includes(v)
    ? (v as LinkVariant)
    : undefined;
};

/**
 * Maps the `card-layout` attr to a valid ArticleCard layout for dynamically
 * fetched cards (`ids`/`category`/`latest`). Static `[hen-ui-article-card]`
 * children set their own `layout` directly. Invalid/omitted = undefined → the
 * card's default `stacked` layout.
 */
const parseCardLayout = (
  val?: string,
): ArticleCardProps['layout'] | undefined => {
  const v = (val ?? '').toLowerCase().trim();
  return v === 'overlay' || v === 'stacked' ? v : undefined;
};

const parseGridGap = (val?: string): GridGap | undefined => {
  const v = (val ?? '').toLowerCase().trim();
  return (GRID_GAPS as readonly string[]).includes(v)
    ? (v as GridGap)
    : undefined;
};

/**
 * ArticleRowShortcode - Shortcode wrapper for ArticleRow composite component
 *
 * Displays article collections in various layouts: hero, grid (2/3/4-col), or carousel.
 *
 * Supported shortcode attributes:
 * - variant: 'hero' | '2-col' | '3-col' | '4-col' | 'carousel' (required)
 * - hero-image: string - Background image URL for hero variant
 * - hero-brow: string - Eyebrow text for hero variant
 * - hero-title: string - Title text for hero variant
 * - hero-subtext: string - Subtitle text for hero variant
 * - hero-action-label: string - CTA button label for hero variant
 * - hero-action-href: string - CTA button link for hero variant
 * - hero-action-target: string - Native anchor target for the hero CTA only
 * - hero-action-data-hen-location / hero-action-data-hen-name: authored eventing metadata placed on the hero CTA
 * - align: `left` | `center` - Overlay text alignment (hero only, default center)
 *
 * Dynamic attributes (fetches articles from CMS):
 * - ids: string - Comma-separated post IDs to fetch
 * - category: string - Comma-separated category slugs (AND logic: posts must match ALL)
 * - order: 'updated' | 'published' | 'custom' - Sort order (default: 'updated')
 * - limit: string - Max number of articles to fetch (default: '8')
 * - size: 'lg' | 'md' | 'sm' | 'compact' - Card size applied to all dynamically fetched cards
 * - border: 'none' | 'padding' | 'stroke' | 'shadow' - Border style applied to all dynamically fetched cards (`shadow` = always-on drop shadow; for excerpt rails prefer `none`/`padding`/`stroke` and use hover elevation only)
 * - show-image: 'true' | 'false' (optional, default: true) - hide featured image on all dynamically fetched cards while keeping image URL from CMS
 * - card-variant: `excerpt` — post excerpt under the title (CMS `content` / WordPress excerpt). Omit or `byline` for author + date.
 * - card-layout: `overlay` | `stacked` — layout applied to all dynamically fetched cards. `overlay` = full-bleed image with title/byline overlaid (Figma Article Row tile, e.g. Trends rails); omit/`stacked` = image on top, text below. Static `[hen-ui-article-card layout="overlay"]` children set this per-card instead. Ignored when `variant="hero"` (the hero overlay has its own layout).
 * - hover-style: `shadow` | `none` — row-level `cardHoverStyle` on `ArticleRow` (dynamic `articles` or static `[hen-ui-article-card]` children). Default: omit → cards use `ArticleCard` default `none`. Per-card override not supported from shortcode.
 * - link-variant: `primary` | `secondary` | `tertiary` | `default` | `black` | `white` | `subtext` — color variant for the "View All" CTA `Link`. Omit to keep each variant's built-in treatment (carousel = primary; grid/vertical = base link color). Use `primary` to brand the CTA with the tenant `primary-700`.
 *
 * - latest: string - When truthy (e.g. "true"), fetches latest posts without category filter
 *
 * If any of ids, category, or latest is set, a CMS fetch runs and its results take full priority as the content source.
 * For the hero variant, the first fetched post populates hero-title, hero-image, and hero-action-href. hero-subtext has no CMS equivalent and only renders when provided explicitly.
 * Static hero-* attrs override their CMS counterpart when both are present. hero-brow and hero-action-label have no CMS equivalent.
 * If none of those attributes are set, no CMS fetch occurs and static [hen-ui-article-card] children are rendered as-is.
 *
 * @example Dynamic - Fetch by IDs:
 * [hen-ui-article-row variant="3-col" ids="123,456,789"][/hen-ui-article-row]
 *
 * @example Dynamic - Fetch by categories (AND logic):
 * [hen-ui-article-row variant="carousel" category="psychology,rankings" limit="6" order="updated"][/hen-ui-article-row]
 *
 * @example Dynamic - Fetch latest posts:
 * [hen-ui-article-row variant="4-col" latest="true" limit="8" order="published"][/hen-ui-article-row]
 *
 * @example Static children (unchanged):
 * [hen-ui-article-row variant="3-col"]
 *   [hen-ui-article-card image-src="/img1.jpg" image-alt="Article 1" title="Title 1" href="/article-1"][/hen-ui-article-card]
 * [/hen-ui-article-row]
 */
export const ArticleRowShortcode: ShortCode<
  ArticleRowShortcodeProps,
  TransformedItemPost[]
> = {
  getEnrichedData: async (
    _content: string,
    context?: EnrichmentContext,
  ): Promise<TransformedItemPost[]> => {
    const attrs = context?.attrs ?? {};
    const config = context?.config;
    if (!config?.cmsUrl || !config?.postTypeGql) return [];

    const { cmsUrl, postTypeGql } = config;
    const ids = attrs['ids'];
    const category = attrs['category'];
    const latest = attrs['latest'];
    const order =
      (attrs['order'] as 'updated' | 'published' | 'custom') || undefined;
    const limit = attrs['limit'];
    const filters = { limit, orderBy: order };

    if (ids) {
      const idList = ids
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      if (!idList.length) return [];
      const result = await fetchEmbeddedPosts(idList, config);
      if (!result) return [];
      const posts = idList
        .map((id) => result[id])
        .filter((p): p is TransformedItemPost => p != null);
      return withDefaultContributors(posts, config);
    }

    const parsedLatest = parseTruthy(latest);
    if (!category && !parsedLatest) return [];

    const categories = category
      ? category
          .split(',')
          .map((c) => c.trim())
          .filter(Boolean)
      : [];
    if (!categories.length && !parsedLatest) return [];
    const result = await fetchEmbeddedPostsByCat(
      categories,
      filters,
      postTypeGql,
      cmsUrl,
      ...(config.fallbackPostImage ? [config.fallbackPostImage] : []),
    );
    return withDefaultContributors(result ?? [], config);
  },

  component: ({
    variant,
    'hero-image': heroImage,
    'hero-brow': heroBrow,
    'hero-title': heroTitle,
    'hero-subtext': heroSubtext,
    'hero-action-label': heroActionLabel,
    'hero-action-href': heroActionHref,
    'hero-action-target': heroActionTarget,
    'hero-action-data-hen-location': heroActionDataHenLocation,
    'hero-action-data-hen-name': heroActionDataHenName,
    align: alignAttr,
    ids,
    category,
    latest,
    size,
    border,
    'show-image': showImageAttr,
    'card-variant': cardVariantAttr,
    'card-layout': cardLayoutAttr,
    'show-brow': showBrowAttr,
    'hover-style': hoverStyleAttr,
    link,
    'link-label': linkLabel,
    'link-variant': linkVariantAttr,
    gap: gapAttr,
    enrichments,
    children,
  }) => {
    const linkVariant = parseLinkVariant(linkVariantAttr);
    const cardLayout = parseCardLayout(cardLayoutAttr);
    const gridGap = parseGridGap(gapAttr);
    const enrichedArticles = enrichments?.data as
      | TransformedItemPost[]
      | null
      | undefined;

    const parsedLatest = parseTruthy(latest);
    const hasIds = Boolean(
      ids
        ?.split(',')
        .map((s) => s.trim())
        .filter(Boolean).length,
    );
    const hasCategory = Boolean(
      category
        ?.split(',')
        .map((s) => s.trim())
        .filter(Boolean).length,
    );
    const hasDynamicAttrs = hasIds || hasCategory || parsedLatest;

    if (variant === 'hero') {
      const dynamicPost = hasDynamicAttrs ? enrichedArticles?.[0] : undefined;
      const heroAlign = parseHeroOverlayAlign(alignAttr);
      const heroMetadataAttrs = pickHenMetadataAttrs({
        'data-hen-location': heroActionDataHenLocation,
        'data-hen-name': heroActionDataHenName,
      });
      const resolvedHref = heroActionHref || dynamicPost?.link;
      const heroAction = heroActionLabel
        ? {
            label: heroActionLabel,
            href: resolvedHref,
            ...(heroActionTarget && resolvedHref
              ? {
                  target: heroActionTarget,
                  rel:
                    heroActionTarget === '_blank'
                      ? 'noopener noreferrer'
                      : undefined,
                }
              : {}),
            ...heroMetadataAttrs,
          }
        : undefined;
      return (
        <ArticleRow
          variant={variant}
          heroImage={heroImage || dynamicPost?.image || ''}
          heroBrow={heroBrow}
          heroTitle={heroTitle || dynamicPost?.title}
          heroSubtext={heroSubtext}
          heroAlign={heroAlign}
          heroAction={heroAction}
        >
          {children}
        </ArticleRow>
      );
    }

    if (enrichedArticles?.length) {
      const showImage = parseShowImage(showImageAttr);
      const cardVariant = parseArticleRowVariant(cardVariantAttr);
      const showBrow = parseShowImage(showBrowAttr);
      const hoverNorm = String(hoverStyleAttr ?? '')
        .toLowerCase()
        .trim();
      const cardHoverStyle: ArticleCardProps['hoverStyle'] | undefined =
        hoverNorm === 'shadow'
          ? 'shadow'
          : hoverNorm === 'none'
            ? 'none'
            : undefined;
      const articles = enrichedArticles.map((p) => ({
        ...toArticleCardProps(p, { variant: cardVariant, layout: cardLayout }),
        size,
        border,
        showImage,
        ...(showBrow ? {} : { brow: undefined }),
        ...(cardHoverStyle === 'shadow'
          ? { hoverStyle: 'shadow' as const }
          : {}),
      }));
      return (
        <ArticleRow
          variant={variant}
          articles={articles}
          cardHoverStyle={cardHoverStyle}
          link={link}
          linkLabel={linkLabel}
          linkVariant={linkVariant}
          gap={gridGap}
        />
      );
    }

    if (hasDynamicAttrs) {
      return (
        <ArticleRow
          variant={variant}
          articles={[]}
          link={link}
          linkLabel={linkLabel}
          linkVariant={linkVariant}
          gap={gridGap}
        />
      );
    }

    return (
      <ArticleRow
        variant={variant}
        link={link}
        linkLabel={linkLabel}
        linkVariant={linkVariant}
        gap={gridGap}
      >
        {children}
      </ArticleRow>
    );
  },
};
